const TIMER_SELECTOR = '.restock-rocket-preorder-countdown-timer';
const BOX_SELECTOR = '.countdown-box[data-unit="days"], .countdown-box[data-unit="hours"], .countdown-box[data-unit="mins"], .countdown-box[data-unit="secs"]';
const ANIMATION_MS = 260;
const states = new Set();
const stateByBox = new WeakMap();
const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

const createElement = (className, text = '') => {
  const element = document.createElement('span');
  element.className = className;
  element.textContent = text;
  return element;
};

const readValue = (box) => {
  const value = String(box.textContent || '').trim().replace(/\s+/g, '');
  return /^\d+$/.test(value) ? value : '';
};

const hasProviderHiddenState = (element) => (
  element?.hidden
  || element?.getAttribute('aria-hidden') === 'true'
  || element?.style?.display === 'none'
  || element?.style?.visibility === 'hidden'
);

const timerIsActive = (box) => {
  const timer = box.closest(TIMER_SELECTOR);
  if (!timer || hasProviderHiddenState(box)) return false;

  let element = box.parentElement;
  while (element) {
    if (hasProviderHiddenState(element)) return false;
    const styles = window.getComputedStyle(element);
    if (styles.display === 'none' || styles.visibility === 'hidden') return false;
    if (element === timer) return true;
    element = element.parentElement;
  }

  return false;
};

const clearDigitAnimation = (digit, { settle = true } = {}) => {
  if (digit.timeout) {
    window.clearTimeout(digit.timeout);
    digit.timeout = 0;
  }

  if (settle && digit.node.classList.contains('is-stepping')) {
    digit.current.textContent = digit.next.textContent;
  }

  digit.node.classList.remove('is-stepping');
};

const buildDigits = (state, value) => {
  state.digits.forEach((digit) => clearDigitAnimation(digit));
  state.digits = [...value].map((character) => {
    const node = createElement('landing-countdown__digit');
    const current = createElement('landing-countdown__digit-current', character);
    const next = createElement('landing-countdown__digit-next', character);
    node.dataset.value = character;
    node.append(current, next);
    return { current, next, node, timeout: 0 };
  });
  state.overlay.replaceChildren(...state.digits.map((digit) => digit.node));
};

const showProviderValue = (state) => {
  state.digits.forEach((digit) => clearDigitAnimation(digit));
  state.previous = '';
  state.overlay?.remove();
  state.overlay = null;
  state.digits = [];
  state.box.classList.remove('stoq-preorder-countdown__source');
};

const ensureOverlay = (state) => {
  if (state.overlay?.isConnected) return;

  const overlay = createElement('stoq-preorder-countdown__digits landing-countdown');
  overlay.dataset.stoqCountdownOverlay = '';
  overlay.setAttribute('aria-hidden', 'true');
  state.box.insertAdjacentElement('afterend', overlay);
  state.box.classList.add('stoq-preorder-countdown__source');
  state.overlay = overlay;
  state.previous = '';
  state.digits = [];
};

const render = (state, { animate = true } = {}) => {
  const value = readValue(state.box);

  if (!value || !timerIsActive(state.box)) {
    showProviderValue(state);
    return;
  }

  ensureOverlay(state);
  const canAnimate = animate && state.previous && !document.hidden && !reducedMotion.matches;

  if (state.digits.length !== value.length) {
    buildDigits(state, value);
  } else {
    state.digits.forEach((digit, index) => {
      const nextValue = value[index];
      if (digit.node.dataset.value === nextValue) return;

      clearDigitAnimation(digit);
      digit.node.dataset.value = nextValue;
      digit.next.textContent = nextValue;

      if (!canAnimate) {
        digit.current.textContent = nextValue;
        return;
      }

      void digit.node.offsetWidth;
      digit.node.classList.add('is-stepping');
      digit.timeout = window.setTimeout(() => {
        digit.current.textContent = nextValue;
        digit.next.textContent = nextValue;
        digit.node.classList.remove('is-stepping');
        digit.timeout = 0;
      }, ANIMATION_MS);
    });
  }

  state.previous = value;
};

const destroy = (state) => {
  if (!states.has(state)) return;
  state.observer.disconnect();
  showProviderValue(state);
  states.delete(state);
  stateByBox.delete(state.box);
};

const enhanceBox = (box) => {
  if (!(box instanceof HTMLElement) || stateByBox.has(box)) return;

  const state = {
    box,
    digits: [],
    observer: null,
    overlay: null,
    previous: '',
  };
  state.observer = new MutationObserver(() => render(state));
  state.observer.observe(box, {
    attributes: true,
    attributeFilter: ['aria-hidden', 'hidden', 'style'],
    characterData: true,
    childList: true,
    subtree: true,
  });
  stateByBox.set(box, state);
  states.add(state);
  render(state, { animate: false });
};

const discover = (scope = document) => {
  const closestTimer = scope.closest?.(TIMER_SELECTOR);
  const timers = [
    ...(closestTimer ? [closestTimer] : []),
    ...(scope.matches?.(TIMER_SELECTOR) ? [scope] : []),
    ...(scope.querySelectorAll?.(TIMER_SELECTOR) || []),
  ];

  new Set(timers).forEach((timer) => {
    timer.querySelectorAll(BOX_SELECTOR).forEach(enhanceBox);
  });
};

const reconcile = () => {
  states.forEach((state) => {
    if (!state.box.isConnected || !state.box.closest(TIMER_SELECTOR)) {
      destroy(state);
      return;
    }

    state.digits.forEach((digit) => clearDigitAnimation(digit));
    render(state, { animate: false });
  });
};

discover();

const documentObserver = new MutationObserver((mutations) => {
  let timerStateChanged = false;
  mutations.forEach((mutation) => {
    if (
      mutation.type === 'attributes'
      && mutation.target.matches(`${TIMER_SELECTOR}, .countdown-units, .countdown-unit, .countdown-box`)
    ) {
      timerStateChanged = true;
    }
    mutation.addedNodes.forEach((node) => {
      if (node instanceof Element && !node.matches('[data-stoq-countdown-overlay]')) {
        discover(node);
      }
    });
  });
  if (timerStateChanged) {
    reconcile();
    return;
  }
  states.forEach((state) => {
    if (!state.box.isConnected || !state.box.closest(TIMER_SELECTOR)) {
      destroy(state);
    } else if (state.overlay && !state.overlay.isConnected) {
      render(state, { animate: false });
    }
  });
});

documentObserver.observe(document.documentElement, {
  attributes: true,
  attributeFilter: ['aria-hidden', 'class', 'hidden', 'style'],
  childList: true,
  subtree: true,
});
document.addEventListener('visibilitychange', reconcile);
reducedMotion.addEventListener?.('change', reconcile);

// Render plain provider messages in the theme's native notice component.
// Offers with interactive/rich content retain their original provider notice.
const notices = new Map();
const elementIsActiveWithin = (element, boundary) => {
  if (!element) return false;
  while (element && element !== boundary) {
    if (hasProviderHiddenState(element)) return false;
    const styles = window.getComputedStyle(element);
    if (styles.display === 'none' || styles.visibility === 'hidden') return false;
    element = element.parentElement;
  }

  return element === boundary;
};
const preorderButtonIsActive = (source) => {
  const scope = source.closest('form') || source.closest('[data-product-section]') || document;
  const boundary = scope instanceof Element ? scope : document.documentElement;
  return elementIsActiveWithin(scope.querySelector('.restock-rocket-preorder-button'), boundary);
};
const noticeSourceIsActive = (source) => {
  if (hasProviderHiddenState(source)) return false;
  const boundary = source.closest('form') || source.closest('[data-product-section]') || document.documentElement;
  const enhanced = source.classList.contains('stoq-preorder__provider-notice');
  if (enhanced) source.classList.remove('stoq-preorder__provider-notice');
  try {
    return elementIsActiveWithin(source, boundary);
  } finally {
    if (enhanced) source.classList.add('stoq-preorder__provider-notice');
  }
};
const syncPreorderForms = () => {
  document.querySelectorAll('.coffee-product__form').forEach((form) => {
    const active = elementIsActiveWithin(
      form.querySelector('.restock-rocket-preorder-button'),
      form,
    );
    form.dataset.stoqPreorderActive = String(active);
  });
};
// Preserve provider paragraph boundaries without injecting provider HTML.
const readNoticeText = (element) => {
  if (!element) return '';
  const copy = element.cloneNode(true);
  copy.querySelectorAll('br').forEach((node) => node.replaceWith('\n'));
  copy.querySelectorAll('p, div, li').forEach((node) => node.append('\n\n'));
  return copy.textContent.trim().replace(/\n{3,}/g, '\n\n');
};
const syncNotice = (source, state) => {
  const summary = source.querySelector('.preorder-description-text');
  const details = [...source.querySelectorAll('.preorder-detail-text')];
  const interactive = source.querySelector('a, button, input, select, textarea, img, video, iframe');
  const text = readNoticeText(summary);
  const template = document.querySelector('[data-stoq-notice-template]');
  const detailTemplate = document.querySelector('[data-stoq-detail-template]');
  if (
    !preorderButtonIsActive(source)
    || !noticeSourceIsActive(source)
    || interactive
    || !template
    || (details.length > 0 && !detailTemplate)
    || (!text && !details.length)
  ) {
    state.panel?.remove();
    state.panel = null;
    if (source.classList.contains('stoq-preorder__provider-notice')) {
      source.classList.remove('stoq-preorder__provider-notice');
    }
    return;
  }
  if (!state.panel?.isConnected) {
    state.panel = template.content.firstElementChild.cloneNode(true);
    source.insertAdjacentElement('afterend', state.panel);
  }
  const summaryTarget = state.panel.querySelector('[data-stoq-notice-summary]');
  if (summaryTarget) summaryTarget.textContent = text;
  state.panel.querySelector('.stoq-preorder__eyebrow').textContent = source.closest('[data-stoq-notice-label]')?.dataset.stoqNoticeLabel || 'Preorder details';
  const list = state.panel.querySelector('[data-stoq-notice-details]');
  list.replaceChildren(...details.map((detail) => {
    const row = detailTemplate.content.firstElementChild.cloneNode(true);
    row.querySelector('span:last-child').textContent = detail.textContent.trim();
    return row;
  }));
  if (!source.classList.contains('stoq-preorder__provider-notice')) {
    source.classList.add('stoq-preorder__provider-notice');
  }
};
const discoverNotices = () => {
  document.querySelectorAll('.restock-rocket-preorder-description').forEach((source) => {
    if (notices.has(source)) return;
    const state = { panel: null, observer: null };
    state.observer = new MutationObserver(() => syncNotice(source, state));
    state.observer.observe(source, {
      attributes: true,
      attributeFilter: ['aria-hidden', 'hidden', 'style'],
      childList: true,
      characterData: true,
      subtree: true,
    });
    notices.set(source, state);
    syncNotice(source, state);
  });
  notices.forEach((state, source) => {
    if (source.isConnected) return;
    state.observer.disconnect();
    state.panel?.remove();
    notices.delete(source);
  });
};
const syncNotices = () => {
  notices.forEach((state, source) => {
    if (source.isConnected) syncNotice(source, state);
  });
};
const includesProviderHook = (node) => (
  node instanceof Element
  && (
    node.matches('.restock-rocket-preorder-description, .restock-rocket-preorder-button')
    || node.querySelector('.restock-rocket-preorder-description, .restock-rocket-preorder-button')
  )
);
const providerClassNames = (value) => String(value || '')
  .split(/\s+/)
  .filter((name) => name && name !== 'stoq-preorder__provider-notice')
  .sort()
  .join(' ');
const providerStateMutation = (mutation) => {
  if (mutation.type !== 'attributes') {
    return [...mutation.addedNodes, ...mutation.removedNodes].some(includesProviderHook);
  }

  if (
    mutation.attributeName === 'class'
    && mutation.target.matches('.restock-rocket-preorder-description')
    && providerClassNames(mutation.oldValue) === providerClassNames(mutation.target.className)
  ) {
    return false;
  }

  return includesProviderHook(mutation.target)
    || mutation.target.matches('.restock-rocket-button-container')
    || Boolean(mutation.target.closest('.restock-rocket-preorder-description'));
};
discoverNotices();
syncPreorderForms();
new MutationObserver((mutations) => {
  const providerStateChanged = mutations.some(providerStateMutation);
  if (!providerStateChanged) return;
  syncPreorderForms();
  discoverNotices();
  syncNotices();
}).observe(document.documentElement, {
  attributes: true,
  attributeOldValue: true,
  attributeFilter: ['aria-hidden', 'class', 'hidden', 'style'],
  childList: true,
  subtree: true,
});

document.addEventListener('restock-rocket:loaded', () => {
  discover();
  syncPreorderForms();
  discoverNotices();
  syncNotices();
});
